import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Box, Avatar, Typography, List, ListItem, ListItemIcon, ListItemText, Tooltip, Paper, Divider, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Button } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EditIcon from "@mui/icons-material/Edit";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import GroupIcon from "@mui/icons-material/Group";
import ChatIcon from "@mui/icons-material/Chat";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import DescriptionIcon from "@mui/icons-material/Description";
import PrivacyTipIcon from "@mui/icons-material/PrivacyTip";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import IosShareIcon from '@mui/icons-material/IosShare';
import VideocamIcon from '@mui/icons-material/Videocam';
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../state/state";
import { selectOriginalUID, selectActiveRole, selectAvailableRoles, checkUserInFirestore, selectEffectiveUID } from "../../../../state/UserStatesV2/userSliceV2";
import { RootState } from "../../../../state/state";
import { openModal } from "../../../../state/ModalSlice/ModalSlice";
import { checkZoomAuth, revokeZoomAccess } from "../../../../state/ZoomSlice/zoomSlice";
import { getZoomAuthUrl } from "../../../../lib/zoom/config";
import { toast } from 'react-hot-toast';
import { getDocs, query, collection, where } from 'firebase/firestore';
import { db } from '../../../../Config/firebase';
import { Syllabus } from '../../../../state/subscriptionTableState/subscriptionTableSlice';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import axios from 'axios';
import { SECRETS_ENV } from '../../../../Config/firebase';
// Add Zoom enabled flag - now using dynamic check
import { isZoomEnabledForProvider } from '../../../../Config/firebase';
// Add Google Meet enabled flag
const GOOGLE_MEET_ENABLED = import.meta.env.VITE_GOOGLE_MEET_OPEN === 'TRUE';
import { FLAVOR } from '../../../../Config/firebase';
import { checkGoogleMeetAuth, revokeGoogleMeetAccess, shouldCheckGoogleMeetAuth } from "../../../../state/GoogleMeetSlice/googleMeetSlice";

const API_ENDPOINT = SECRETS_ENV.API_END_POINT;

interface ZoomAuthResponse {
  isAuthenticated: boolean;
  email?: string;
  name?: string;
  personalMeetingUrl?: string;
}

export default function Profile() {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const originalUID = useAppSelector(selectOriginalUID);
  const effectiveUID = useAppSelector(selectEffectiveUID);
  const activeRole = useAppSelector(selectActiveRole);
  const availableRoles = useAppSelector(selectAvailableRoles);
  const userData = useAppSelector((state: RootState) => state.userSliceV2.data);
  const userStatus = useAppSelector((state: RootState) => state.userSliceV2.status);
  const [showMeetingIntegrationsModal, setShowMeetingIntegrationsModal] = useState(false);
  const [showZoomModal, setShowZoomModal] = useState(false);
  const [showZoomDisconnectModal, setShowZoomDisconnectModal] = useState(false);
  const [showGoogleDisconnectModal, setShowGoogleDisconnectModal] = useState(false);
  const [zoomAuthLoading, setZoomAuthLoading] = useState(false);
  const [googleAuthLoading, setGoogleAuthLoading] = useState(false);
  const zoomState = useAppSelector((state: RootState) => state.zoomSlice.data) || {
    isAuthenticated: false,
    userEmail: null
  };
  const zoomStatus = useAppSelector((state: RootState) => state.zoomSlice.status);

  // Google Meet state from Redux
  const googleMeetState = useAppSelector((state: RootState) => state.googleMeetSlice.data) || {
    isAuthenticated: false,
    userEmail: null,
    userName: null
  };

  // Use ref to track previous authentication state to avoid circular dependency
  const [lastGoogleAuthCheck, setLastGoogleAuthCheck] = useState<number>(0);
  const GOOGLE_AUTH_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  // State for custom branding
  const [isCustomBrandingExists, setIsCustomBrandingExists] = useState<boolean>(false);
  const customBrandingModalOpen = useAppSelector((state: RootState) => state.ModalSlice.CustomBrandingModalOpen);

  // Add a state for syllabi at the top level after other states
  const [userSyllabi, setUserSyllabi] = useState<Syllabus[]>([]);
  const [syllabusLoading, setSyllabusLoading] = useState<boolean>(false);
  const [syllabusModalOpen, setSyllabusModalOpen] = useState<boolean>(false);

  // Add state for zoom enabled status
  const [zoomEnabled, setZoomEnabled] = useState<boolean>(false);

  const isCoachHost = useMemo(() => {
    const hostname = window.location.host.split(":")[0];
    return hostname.split(".")[0] === "coach";
  }, []);

  // Google Meet authentication functions (defined early to be used in useEffects)
  // Backend now stores Google tokens in: ServiceProviders/{userId}/MeetingProviders/google
  // Expected API response should include: { isAuthenticated, email, providerEmail, name, displayName, firstName, lastName, etc. }
  const checkGoogleAuth = useCallback(async (forceCheck: boolean = false) => {
    if (!effectiveUID) {
      return;
    }

    // Use the Redux slice's smart checking logic
    if (!forceCheck && !shouldCheckGoogleMeetAuth()) {
      console.log('🔄 Skipping Google Meet auth check - using persisted state');
      return;
    }

    try {
      console.log('🔄 Checking Google Meet auth for:', effectiveUID);
      await dispatch(checkGoogleMeetAuth(effectiveUID));
      setLastGoogleAuthCheck(Date.now());
    } catch (error) {
      console.error('❌ Error checking Google Meet auth:', error);
      setLastGoogleAuthCheck(0); // Reset cache on error
    }
  }, [effectiveUID, dispatch]);

  // Function to check if custom branding exists
  const checkCustomBrandingExists = useCallback(async () => {
    if (!originalUID) return;

    try {
      const querySnapshot = await getDocs(
        query(
          collection(db, 'CustomBranding'),
          where('providerReference', '==', originalUID)
        )
      );
      const exists = !querySnapshot.empty;
      // Only update state if the value has changed
      if (exists !== isCustomBrandingExists) {
        setIsCustomBrandingExists(exists);
      }
    } catch (error) {
      console.error('Error checking custom branding:', error);
      if (isCustomBrandingExists) { // Only update if current value is true
        setIsCustomBrandingExists(false);
      }
    }
  }, [originalUID, isCustomBrandingExists]);

  useEffect(() => {
    const loadUserProfile = async () => {
      if (originalUID) {


        // If we don't have user data or it's not loaded, fetch it
        if (!userData || userStatus === 'not_loaded') {

          await dispatch(checkUserInFirestore(originalUID));
        }
      }
    };

    loadUserProfile();
  }, [originalUID, dispatch, userData, userStatus]);

  // Debug logs for data updates
  // useEffect(() => {
  //   console.log("userData updated:", userData);
  //   console.log("userStatus updated:", userStatus);
  //   console.log("activeRole updated:", activeRole);
  //   console.log("availableRoles updated:", availableRoles);
  // }, [userData, userStatus, activeRole, availableRoles]);

  useEffect(() => {
    if (originalUID) {
      checkCustomBrandingExists();
    }
  }, [originalUID, customBrandingModalOpen, checkCustomBrandingExists]);

  useEffect(() => {
    if (effectiveUID) {
      console.log('🔍 Profile: Checking Zoom auth for:', effectiveUID);
      dispatch(checkZoomAuth(effectiveUID));
      // Also check Google Meet auth status using effectiveUID (force check to get fresh data)
      checkGoogleAuth(true);
    }
  }, [effectiveUID, dispatch, checkGoogleAuth]);

  // Check zoom enabled status
  useEffect(() => {
    if (effectiveUID) {
      const enabled = isZoomEnabledForProvider();
      setZoomEnabled(enabled);
      console.log('🔍 Profile: Zoom enabled for provider:', effectiveUID, '=', enabled);
    }
  }, [effectiveUID]);

  // Add a useEffect to fetch syllabi when the component mounts
  useEffect(() => {
    const fetchUserSyllabi = async () => {
      if (!effectiveUID) return;

      try {
        setSyllabusLoading(true);
        const syllabusCollection = collection(db, 'ServiceProviders', effectiveUID, 'Syllabi');
        const querySnapshot = await getDocs(syllabusCollection);

        const syllabi: Syllabus[] = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data() as Syllabus;
          syllabi.push({
            ...data,
            id: doc.id
          });
        });

        setUserSyllabi(syllabi);
        setSyllabusLoading(false);
      } catch (error) {
        console.error('Error loading syllabi:', error);
        toast.error('Failed to load syllabi');
        setSyllabusLoading(false);
      }
    };

    fetchUserSyllabi();
  }, [effectiveUID]);

  // Combine auth checking effects into one
  useEffect(() => {
    let mounted = true;
    const checkAuthStatuses = async () => {
      if (!effectiveUID || !mounted) return;

      try {
        console.log('🔄 Profile: Periodic auth check for:', effectiveUID);
        // Check Zoom
        const zoomResponse = await dispatch(checkZoomAuth(effectiveUID)).unwrap() as ZoomAuthResponse;
        if (mounted && zoomResponse.isAuthenticated && zoomResponse.email && !zoomState.isAuthenticated) {
          toast.success(`Connected to Zoom as ${zoomResponse.email}`);
        }

        // Check Google Meet (with cache)
        const now = Date.now();
        if (mounted && (!lastGoogleAuthCheck || (now - lastGoogleAuthCheck) >= GOOGLE_AUTH_CACHE_DURATION)) {
          await checkGoogleAuth();
        }
      } catch (error) {
        console.error('❌ Error checking auth statuses:', error);
      }
    };

    // Check immediately
    checkAuthStatuses();

    // Set up interval for periodic checks - check more frequently initially
    const interval = setInterval(checkAuthStatuses, 30 * 1000); // Check every 30 seconds

    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [effectiveUID, dispatch, zoomState.isAuthenticated, lastGoogleAuthCheck, checkGoogleAuth]);

  // Handle Zoom OAuth
  const handleZoomAuth = async () => {
    if (!effectiveUID) return;

    setZoomAuthLoading(true);
    try {
      const authUrl = getZoomAuthUrl(effectiveUID);
      // Instead of opening in iframe, redirect the current window
      window.location.href = authUrl;
    } catch (error) {
      console.error('Error initiating Zoom auth:', error);
      toast.error('Failed to initiate Zoom authentication');
    } finally {
      setZoomAuthLoading(false);
    }
  };

  // Handle Zoom disconnect
  const handleZoomDisconnect = async () => {
    if (!effectiveUID) return;

    setShowZoomDisconnectModal(true);
  };

  const confirmZoomDisconnect = async () => {
    if (!effectiveUID) return;

    try {
      await dispatch(revokeZoomAccess(effectiveUID));
      setShowMeetingIntegrationsModal(false);
      setShowZoomDisconnectModal(false);
      toast.success('Successfully disconnected from Zoom');
    } catch (error) {
      console.error('Error disconnecting Zoom:', error);
      toast.error('Failed to disconnect from Zoom');
    }
  };

  const handleGoogleAuth = async () => {
    if (!effectiveUID) return;

    setGoogleAuthLoading(true);
    try {
      const redirectUrl = `${window.location.origin}/profile`;
      const response = await axios.post(`${API_ENDPOINT}/api/gmeet/get-auth-url`, {
        userId: effectiveUID,
        redirectUrl: encodeURIComponent(redirectUrl)
      });

      // Redirect to Google OAuth
      window.location.href = response.data.authUrl;
    } catch (error) {
      console.error('Error initiating Google Meet auth:', error);
      toast.error('Failed to initiate Google Meet authentication');
    } finally {
      setGoogleAuthLoading(false);
    }
  };

  const handleGoogleDisconnect = async () => {
    setShowGoogleDisconnectModal(true);
  };

  const confirmGoogleDisconnect = async () => {
    if (!effectiveUID) return;

    try {
      await dispatch(revokeGoogleMeetAccess(effectiveUID));
      setLastGoogleAuthCheck(0); // Reset cache
      setShowMeetingIntegrationsModal(false);
      setShowGoogleDisconnectModal(false);
      toast.success('Successfully disconnected from Google Meet');
    } catch (error) {
      console.error('Error disconnecting Google Meet:', error);
      toast.error('Failed to disconnect from Google Meet');
    }
  };

  // Handle OAuth redirect parameters for both platforms
  useEffect(() => {
    // Handle Zoom OAuth
    const zoomStatus = searchParams.get('zoom');
    const zoomError = searchParams.get('error');

    if (zoomStatus === 'success') {
      toast.success('Successfully connected to Zoom');
      navigate('/profile', { replace: true });
    } else if (zoomError) {
      switch (zoomError) {
        case 'no_code':
          toast.error('Authorization code is missing. Please try again.');
          break;
        case 'no_state':
          toast.error('State parameter is missing. Please try again.');
          break;
        case 'token_exchange_failed':
          toast.error('Failed to exchange authorization code. Please try again.');
          break;
        case 'invalid_user':
          toast.error('User account not found. Please try again.');
          break;
        case 'internal_error':
          toast.error('An unexpected error occurred. Please try again later.');
          break;
        default:
          toast.error('Failed to connect Zoom account. Please try again.');
      }
      navigate('/profile', { replace: true });
    }

    // Handle Google Meet OAuth
    const googleAuthSuccess = searchParams.get('googleAuthSuccess');
    if (googleAuthSuccess === 'true') {
      toast.success('Successfully connected to Google Meet');
      checkGoogleAuth(true); // Force refresh Google auth status
      navigate('/profile', { replace: true });
    }

    // Handle meeting integrations modal opening from subscription details page
    const openMeetingIntegrations = searchParams.get('openMeetingIntegrations');
    if (openMeetingIntegrations === 'true') {
      setShowMeetingIntegrationsModal(true);
      // Remove the query parameter from URL
      navigate('/profile', { replace: true });
    }
  }, [searchParams, navigate, checkGoogleAuth]);

  // Memoize handler functions
  const handleUnblockWhatsApp = useCallback(() => {
    navigator.clipboard.writeText("https://wa.me/message/PPRTZCMJ6GEED1").then(() => {
      dispatch(openModal("WhatsappUnblockNotificationOpen"));
    });
  }, [dispatch]);

  const handleSyllabusManagement = useCallback(() => {
    setSyllabusModalOpen(true);
  }, []);

  // Define the profileOptions array with useMemo and all dependencies
  const profileOptions = useMemo(() => [
    {
      icon: EditIcon,
      text: "Edit Profile",
      action: "editServiceProviderModalOpen",
      ownerOnly: true,
      disabledMessage: "Switch to owner account to edit profile"
    },
    // Removing role switching option since it's available in header
    {
      icon: UpgradeIcon,
      text: "Plan Details",
      navigateTo: "/subscription-plan",
      ownerOnly: true,
      disabledMessage: "Switch to owner account to manage plan details",
      devonly: true
    },
    {
      icon: AccountBalanceIcon,
      text: "Bank Accounts",
      action: "AllBankAccountsModalOpen",
      ownerOnly: true,
      disabledMessage: "Switch to owner account to manage bank accounts"
    },
    {
      icon: GroupIcon,
      text: "Manage My Team",
      navigateTo: "/team-management",
      adminOwnerOnly: true,
      disabledMessage: "Only owners and administrators can manage team"
    },
    {
      icon: DescriptionIcon,
      text: "Manage Customer KYC",
      action: "CustomerKYCModalOpen",
      ownerOnly: true,
      disabledMessage: "Switch to owner account to manage customer KYC"
    },
    { icon: IosShareIcon, text: "Export Data", navigateTo: "/exports" },
    // Only show custom branding option when user is using their own account
    ...(!activeRole ? [{
      icon: GroupIcon,
      text: isCustomBrandingExists ? "Edit Custom Branding" : "Custom Branding",
      action: isCustomBrandingExists ? "EditCustomBrandingModalOpen" : "CustomBrandingModalOpen",
    }] : []),
    {
      icon: DescriptionIcon,
      text: "Manage Syllabi",
      handler: handleSyllabusManagement,
      description: userSyllabi.length > 0 ? `${userSyllabi.length} syllabi available` : "No syllabi created yet"
    },
    // Replace separate Zoom integration with unified meeting integrations
    ...((zoomEnabled || GOOGLE_MEET_ENABLED) ? [{
      icon: VideocamIcon,
      text: "Meeting Integrations",
      action: "openMeetingIntegrationsModal",
      adminOwnerOnly: true,
      disabledMessage: "Only administrators and owners can manage meeting integrations",
      description: (() => {
        const connections = [];

        if (zoomEnabled && zoomState.isAuthenticated && zoomState.userEmail) {
          connections.push(`Zoom: ${zoomState.userEmail}`);
        }
        if (GOOGLE_MEET_ENABLED && googleMeetState.isAuthenticated) {
          if (googleMeetState.userName && googleMeetState.userEmail) {
            connections.push(`Google Meet: ${googleMeetState.userName} (${googleMeetState.userEmail})`);
          } else if (googleMeetState.userName) {
            connections.push(`Google Meet: ${googleMeetState.userName}`);
          } else if (googleMeetState.userEmail) {
            connections.push(`Google Meet: ${googleMeetState.userEmail}`);
          } else {
            connections.push(`Google Meet: Connected`);
          }
        }
        if (connections.length === 0) {
          return "No accounts connected";
        }
        return connections.join(" • ");
      })()
    }] : []),
    { icon: ChatIcon, text: "Unblock WhatsApp", handler: handleUnblockWhatsApp },
    ...(isCoachHost
      ? []
      : [
          { icon: SupportAgentIcon, text: "Support", link: "mailto:support@besaksham.com" },
          { icon: DescriptionIcon, text: "Terms of Service", link: "https://hosaksham.in/terms-and-conditions/" },
          { icon: PrivacyTipIcon, text: "Privacy Policy", link: "https://hosaksham.in/privacy-policy/" },
        ]),
  ], [
    availableRoles,
    activeRole,
    isCustomBrandingExists,
    userSyllabi,
    zoomState,
    googleMeetState,
    zoomEnabled,
    handleUnblockWhatsApp,
    handleSyllabusManagement,
    isCoachHost
  ]);

  // Filter out dev-only options if not in development mode
  const filteredProfileOptions = profileOptions.filter(option => !option.devonly || FLAVOR === "DEVELOPMENT");

  const handleAction = (option: any) => {
    // For syllabus management, check for admin or owner role
    if (option.text === "Manage Syllabi") {
      if (!activeRole || activeRole.role === 'Administrator') {
        if (option.handler) {
          option.handler();
        }
      } else {
        toast.error("Only owners and administrators can manage syllabi");
      }
      return;
    }

    // For Zoom integration, allow both Administrator and Owner
    if (option.action === 'openZoomModal') {
      if (activeRole && !['Administrator', 'Owner'].includes(activeRole.role)) {
        toast.error("Only administrators and owners can manage Zoom integration");
        return;
      }
      setShowZoomModal(true);
      return;
    }

    // For Meeting Integrations, allow both Administrator and Owner
    if (option.action === 'openMeetingIntegrationsModal') {
      if (activeRole && !['Administrator', 'Owner'].includes(activeRole.role)) {
        toast.error("Only administrators and owners can manage meeting integrations");
        return;
      }
      setShowMeetingIntegrationsModal(true);
      return;
    }

    // For Team Management, allow both Administrator and Owner
    if (option.text === "Manage My Team") {
      if (activeRole && !['Administrator', 'Owner'].includes(activeRole.role)) {
        toast.error("Only owners and administrators can manage team");
        return;
      }
      // Continue to navigation
    }

    // For other owner-only features, restrict to owner only
    if (option.ownerOnly && activeRole) {
      toast.error("This feature is only available in owner mode");
      return;
    }

    if (option.handler) {
      option.handler();
    } else if (option.navigateTo) {
      navigate(option.navigateTo);
    } else if (option.link) {
      window.open(option.link, "_blank");
    } else if (option.action) {
      if (option.action === 'WhatsappUnblockNotificationOpen') {
        handleUnblockWhatsApp();
      }
      dispatch(openModal(option.action));
    }
  };

  // Debug logs for Zoom state changes
  useEffect(() => {
    console.log('🔍 Profile: Zoom state changed:', {
      isAuthenticated: zoomState.isAuthenticated,
      userEmail: zoomState.userEmail,
      status: zoomStatus
    });
  }, [zoomState.isAuthenticated, zoomState.userEmail, zoomStatus]);

  return (
    <>
      <Box sx={{ maxWidth: "100%", width: { xs: "100%", sm: "90%", md: 900 }, margin: "auto", p: { xs: 2, sm: 3 } }}>
        <Paper elevation={3} sx={{ borderRadius: 4, overflow: "hidden", mb: 4 }}>
          <Box
            sx={{
              background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.light} 90%)`,
              height: 120,
              position: "relative",
            }}
          />
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mt: -7, pb: 3 }}>
            <Avatar
              alt="Profile Picture"
              src={userData?.photo_url}
              sx={{
                width: { xs: 80, sm: 120 },
                height: { xs: 80, sm: 120 },
                border: "4px solid white",
                boxShadow: theme.shadows[3],
              }}
            />
            <Typography variant="h5" sx={{ mt: 2, fontWeight: "bold", fontSize: { xs: "1.25rem", sm: "1.5rem" }, textAlign: "center", px: 2 }}>
              {userData?.display_name}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, fontSize: { xs: "0.75rem", sm: "0.875rem" } }} color="text.secondary">
              {userData?.phone_number}
            </Typography>
            {activeRole && (
              <Box
                sx={{
                  mt: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  py: 1,
                  px: 2,
                  borderRadius: 2,
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  border: '1px solid rgba(0, 0, 0, 0.08)'
                }}
              >
                <GroupIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.primary',
                      fontWeight: 500,
                      lineHeight: 1.2
                    }}
                  >
                    {activeRole.role}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      display: 'block'
                    }}
                  >
                    {activeRole.businessName}
                  </Typography>
                </Box>
              </Box>
            )}
            {!activeRole && <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
              <Tooltip title="Get your QR Code" arrow>
                <Box
                  onClick={() => handleAction({ action: "QRCodeModalOpen" })}
                  sx={{
                    cursor: "pointer",
                    bgcolor: theme.palette.secondary.main,
                    color: "white",
                    py: 0.5,
                    px: 2,
                    borderRadius: 20,
                    display: "flex",
                    alignItems: "center",
                    transition: "all 0.3s",
                    "&:hover": {
                      bgcolor: theme.palette.secondary.dark,
                      transform: "scale(1.05)",
                    },
                  }}
                >
                  <QrCode2Icon sx={{ mr: 1, fontSize: 20 }} />
                  <Typography variant="button" sx={{ fontSize: 14 }}>
                    Get QR Code
                  </Typography>
                </Box>
              </Tooltip>
            </Box>}
          </Box>
        </Paper>

        <Paper elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
          <List>
            {filteredProfileOptions.map((option, index) => {
              // Check if option should be disabled
              const isOwnerOnlyDisabled = option.ownerOnly && activeRole;
              const isAdminOwnerOnlyDisabled = option.adminOwnerOnly && activeRole && !['Administrator', 'Owner'].includes(activeRole.role);
              const isDisabled = isOwnerOnlyDisabled || isAdminOwnerOnlyDisabled;
              
              return (
              <React.Fragment key={index}>
                <ListItem
                  button
                  onClick={() => handleAction(option)}
                  disabled={isDisabled}
                  sx={{
                    py: 1.5,
                    opacity: isDisabled ? 0.6 : 1,
                    "&:hover": {
                      bgcolor: isDisabled ? 'transparent' : theme.palette.action.hover,
                      "& .MuiListItemIcon-root": {
                        color: isDisabled ? 'inherit' : theme.palette.primary.main
                      },
                      "& .MuiTypography-root": {
                        color: isDisabled ? 'inherit' : theme.palette.primary.main
                      },
                    },
                  }}
                >
                  <ListItemIcon>
                    <option.icon color="action" sx={{ fontSize: 24 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={option.text}
                    secondary={
                      isDisabled
                        ? option.disabledMessage
                        : option.description
                    }
                    primaryTypographyProps={{ variant: "body1" }}
                    secondaryTypographyProps={{
                      sx: {
                        color: isDisabled ? 'warning.main' : 'text.secondary',
                        fontSize: '0.75rem'
                      }
                    }}
                  />
                  {!isDisabled && <ChevronRightIcon color="action" sx={{ fontSize: 24 }} />}
                </ListItem>
                {index < filteredProfileOptions.length - 1 && <Divider />}
              </React.Fragment>
            );
            })}
          </List>
        </Paper>

        <Box sx={{ textAlign: "center", mt: 4, color: theme.palette.text.secondary }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Version 1.01
          </Typography>
          <Typography variant="body2">
            Crafted with <span role="img" aria-label="love">
              ❤️
            </span>
          </Typography>
        </Box>
      </Box>

      {/* Meeting Integrations Modal */}
      <Dialog
        open={showMeetingIntegrationsModal}
        onClose={() => setShowMeetingIntegrationsModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Meeting Integrations
        </DialogTitle>
        <DialogContent>
          <div className="py-4 space-y-6">
            <p className="text-sm text-gray-600 mb-6">
              Connect your meeting platforms to create and manage meetings directly from your subscriptions.
            </p>

            {/* Zoom Integration Section */}
            {zoomEnabled && (
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <VideocamIcon sx={{ fontSize: 24, color: '#2D8CFF' }} />
                    <div>
                      <h3 className="font-medium text-gray-900">Zoom</h3>
                      <p className="text-sm text-gray-500">Create and manage Zoom meetings</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {zoomState.isAuthenticated ? (
                      <div className="flex items-center space-x-2 text-green-600 text-sm">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span>Connected</span>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-500">Not connected</span>
                    )}
                  </div>
                </div>

                {zoomState.isAuthenticated && zoomState.userEmail ? (
                  <div className="bg-green-50 border border-green-200 rounded-md p-3 mb-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-green-800 font-medium">
                          Connected as {zoomState.userEmail}
                        </p>
                        <p className="text-xs text-green-600 mt-1">
                          You can now create Zoom meetings from your subscriptions
                        </p>
                      </div>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={handleZoomDisconnect}
                      >
                        Disconnect
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-3">
                    <p className="text-sm text-blue-700 mb-3">
                      Connect your Zoom account to create and manage Zoom meetings directly from your subscriptions.
                    </p>
                    <ul className="list-disc pl-5 space-y-1 text-xs text-blue-600 mb-3">
                      <li>Create instant and scheduled meetings</li>
                      <li>Set up recurring meetings</li>
                      <li>Manage meeting settings</li>
                    </ul>
                    <Button
                      variant="contained"
                      onClick={handleZoomAuth}
                      disabled={zoomAuthLoading}
                      sx={{
                        bgcolor: '#2D8CFF',
                        '&:hover': { bgcolor: '#2681F2' },
                      }}
                    >
                      {zoomAuthLoading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        'Connect Zoom Account'
                      )}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Google Meet Integration Section */}
            {GOOGLE_MEET_ENABLED && (
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <svg className="w-6 h-6 text-[#4285F4]" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12.545,10.239v3.821h5.445c-0.712,2.315-2.647,3.972-5.445,3.972c-3.332,0-6.033-2.701-6.033-6.032s2.701-6.032,6.033-6.032c1.498,0,2.866,0.549,3.921,1.453l2.814-2.814C17.503,2.988,15.139,2,12.545,2C7.021,2,2.543,6.477,2.543,12s4.478,10,10.002,10c8.396,0,10.249-7.85,9.426-11.748L12.545,10.239z" />
                    </svg>
                    <div>
                      <h3 className="font-medium text-gray-900">Google Meet</h3>
                      <p className="text-sm text-gray-500">Create and manage Google Meet meetings</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {googleMeetState.isAuthenticated ? (
                      <div className="flex items-center space-x-2 text-green-600 text-sm">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span>Connected</span>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-500">Not connected</span>
                    )}
                  </div>
                </div>

                {googleMeetState.isAuthenticated ? (
                  <div className="bg-green-50 border border-green-200 rounded-md p-3 mb-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-green-800 font-medium">
                          {googleMeetState.userName || googleMeetState.userEmail ? (
                            <>
                              Connected as {googleMeetState.userName || 'Google Account'}
                              {googleMeetState.userEmail && (
                                <span className="block text-xs text-green-600 font-normal mt-0.5">
                                  {googleMeetState.userEmail}
                                </span>
                              )}
                            </>
                          ) : (
                            <>Google Meet Connected</>
                          )}
                        </p>
                        <p className="text-xs text-green-600 mt-1">
                          You can now create Google Meet meetings from your subscriptions
                        </p>
                      </div>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={handleGoogleDisconnect}
                      >
                        Disconnect
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-3">
                    <p className="text-sm text-blue-700 mb-3">
                      Connect your Google account to create and manage Google Meet meetings directly from your subscriptions.
                    </p>
                    <ul className="list-disc pl-5 space-y-1 text-xs text-blue-600 mb-3">
                      <li>Create instant and scheduled meetings</li>
                      <li>Set up recurring meetings</li>
                      <li>Automatic calendar integration</li>
                    </ul>
                    <Button
                      variant="contained"
                      onClick={handleGoogleAuth}
                      disabled={googleAuthLoading}
                      sx={{
                        bgcolor: '#4285F4',
                        '&:hover': { bgcolor: '#3367d6' },
                      }}
                    >
                      {googleAuthLoading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        'Connect Google Account'
                      )}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowMeetingIntegrationsModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Zoom Disconnect Confirmation Modal */}
      <Dialog
        open={showZoomDisconnectModal}
        onClose={() => setShowZoomDisconnectModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          Disconnect Zoom Account
        </DialogTitle>
        <DialogContent>
          <div className="py-2">
            <div className="flex items-start space-x-3 mb-4">
              <div className="flex-shrink-0">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-700 mb-2">
                  Are you sure you want to disconnect your Zoom account?
                </p>
                <p className="text-sm text-gray-600">
                  This action will:
                </p>
                <ul className="list-disc list-inside text-sm text-gray-600 mt-2 space-y-1">
                  <li>Remove access to create new Zoom meetings</li>
                  <li>Disable Zoom integration for all subscriptions</li>
                  <li>Existing meetings will remain but won't be manageable from this platform</li>
                </ul>
              </div>
            </div>
          </div>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setShowZoomDisconnectModal(false)}
            variant="outlined"
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={confirmZoomDisconnect}
            variant="contained"
            color="error"
            sx={{
              bgcolor: '#dc2626',
              '&:hover': {
                bgcolor: '#b91c1c',
              },
            }}
          >
            Disconnect
          </Button>
        </DialogActions>
      </Dialog>

      {/* Google Meet Disconnect Confirmation Modal */}
      <Dialog
        open={showGoogleDisconnectModal}
        onClose={() => setShowGoogleDisconnectModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          Disconnect Google Meet Account
        </DialogTitle>
        <DialogContent>
          <div className="py-2">
            <div className="flex items-start space-x-3 mb-4">
              <div className="flex-shrink-0">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-700 mb-2">
                  Are you sure you want to disconnect your Google Meet account?
                </p>
                <p className="text-sm text-gray-600">
                  This action will:
                </p>
                <ul className="list-disc list-inside text-sm text-gray-600 mt-2 space-y-1">
                  <li>Remove access to create new Google Meet meetings</li>
                  <li>Disable Google Meet integration for all subscriptions</li>
                  <li>Existing meetings will remain but won't be manageable from this platform</li>
                </ul>
              </div>
            </div>
          </div>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setShowGoogleDisconnectModal(false)}
            variant="outlined"
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={confirmGoogleDisconnect}
            variant="contained"
            color="error"
            sx={{
              bgcolor: '#dc2626',
              '&:hover': {
                bgcolor: '#b91c1c',
              },
            }}
          >
            Disconnect
          </Button>
        </DialogActions>
      </Dialog>

      {/* Zoom Integration Modal */}
      <Dialog
        open={showZoomModal}
        onClose={() => setShowZoomModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {zoomState.isAuthenticated ? "Manage Zoom Integration" : "Connect Zoom Account"}
        </DialogTitle>
        <DialogContent>
          <div className="py-4">
            {zoomState.isAuthenticated ? (
              <div className="space-y-4">
                <div className="flex items-center space-x-2 text-green-600">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Connected to Zoom as {zoomState.userEmail}</span>
                </div>
                <p className="text-sm text-gray-600">
                  Your Zoom account is connected. You can now create and manage Zoom meetings directly from your subscriptions.
                </p>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleZoomDisconnect}
                  fullWidth
                >
                  Disconnect Zoom Account
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Connect your Zoom account to create and manage Zoom meetings directly from your subscriptions.
                  This integration allows you to:
                </p>
                <ul className="list-disc pl-5 space-y-2 text-sm text-gray-600">
                  <li>Create Zoom meetings for your subscriptions</li>
                  <li>Schedule recurring meetings</li>
                  <li>Manage meeting settings and participants</li>
                  <li>Access meeting recordings</li>
                </ul>
                <Button
                  variant="contained"
                  onClick={handleZoomAuth}
                  disabled={zoomAuthLoading}
                  fullWidth
                  sx={{
                    bgcolor: '#2D8CFF',
                    '&:hover': {
                      bgcolor: '#2681F2',
                    },
                  }}
                >
                  {zoomAuthLoading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    <>
                      <VideocamIcon sx={{ mr: 1 }} />
                      Connect with Zoom
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowZoomModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Syllabus Management Modal */}
      <Dialog
        open={syllabusModalOpen}
        onClose={() => setSyllabusModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Select Syllabus to Edit
        </DialogTitle>
        <DialogContent>
          <div className="py-4">
            {syllabusLoading ? (
              <CircularProgress size={24} />
            ) : userSyllabi.length > 0 ? (
              <div className="space-y-4">
                {userSyllabi.map((syllabus) => (
                  <Box
                    key={syllabus.id}
                    sx={{
                      p: 2,
                      mb: 2,
                      border: '1px solid #ddd',
                      borderRadius: 1,
                      position: 'relative',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: 'primary.main',
                        boxShadow: '0 0 0 1px rgba(25, 118, 210, 0.2)',
                        bgcolor: 'rgba(25, 118, 210, 0.04)'
                      }
                    }}
                    onClick={() => {
                      navigate(`/syllabus-editor/${syllabus.id}`);
                      setSyllabusModalOpen(false);
                    }}
                  >
                    <Typography variant="h6">{syllabus.name}</Typography>
                    <Typography variant="caption" display="block" sx={{ mb: 1 }}>
                      Last modified: {new Date(syllabus.lastModified).toLocaleString()}
                    </Typography>
                    <Typography variant="body2">
                      {syllabus.description || 'No description provided'}
                    </Typography>
                    <Box sx={{ mt: 1, display: 'flex', gap: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {syllabus.levels.length} Level(s)
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        •
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {syllabus.levels.reduce((total, level) => total + level.sessions.length, 0)} Session(s)
                      </Typography>
                    </Box>
                  </Box>
                ))}
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => {
                    navigate('/create-syllabus');
                    setSyllabusModalOpen(false);
                  }}
                >
                  Create New Syllabus
                </Button>
              </div>
            ) : (
              <div className="flex flex-col items-center space-y-4">
                <Typography variant="body1" align="center">
                  You haven't created any syllabi yet
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => {
                    navigate('/create-syllabus');
                    setSyllabusModalOpen(false);
                  }}
                >
                  Create Your First Syllabus
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSyllabusModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}